import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  confirmSignUp,
  resendConfirmationCode,
  redirectToSocialSignIn,
  socialConfigured,
} from '../utils/cognito';
import FocusGuard from './FocusGuard';
import PasswordToggle from './PasswordToggle';
import './Auth.css';

const MAX_CLIENT_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 30_000; // 30 seconds client-side soft lockout

const Login: React.FC = () => {
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError]         = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [lockoutSecondsLeft, setLockoutSecondsLeft] = useState(0);

  // When Cognito reports the account isn't verified, switch to the code screen.
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [confirmCode, setConfirmCode] = useState('');
  const [info, setInfo] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();

  // Countdown timer for client-side lockout
  useEffect(() => {
    if (!lockedUntil) return;
    const interval = setInterval(() => {
      const remaining = Math.ceil((lockedUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockedUntil(null);
        setLockoutSecondsLeft(0);
        setFailedAttempts(0);
      } else {
        setLockoutSecondsLeft(remaining);
      }
    }, 500);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const isLockedOut = lockedUntil !== null && Date.now() < lockedUntil;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLockedOut) return;
    setError('');
    setInfo('');
    setIsLoading(true);
    try {
      await login(email, password);
      setFailedAttempts(0);
      navigate('/dashboard');
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === 'UserNotConfirmedException') {
        // Unverified account — send them to the code screen.
        try { await resendConfirmationCode(email); } catch { /* ignore */ }
        setNeedsConfirm(true);
        setInfo('Your email needs verification. We sent you a new code.');
        setIsLoading(false);
        return;
      }

      const message = err instanceof Error ? err.message : 'Login failed';
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      if (message.toLowerCase().includes('too many') || newAttempts >= MAX_CLIENT_ATTEMPTS) {
        const until = Date.now() + LOCKOUT_DURATION_MS;
        setLockedUntil(until);
        setLockoutSecondsLeft(LOCKOUT_DURATION_MS / 1000);
        setError('');
      } else {
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await confirmSignUp(email, confirmCode.trim());
      // Now that the account is confirmed, complete the sign-in with the
      // password already entered.
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Confirmation failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocial = async (provider: 'Google' | 'SignInWithApple') => {
    setError('');
    try {
      await redirectToSocialSignIn(provider); // full-page redirect to Cognito
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start sign-in.');
    }
  };

  // ── Render: confirm-code screen ─────────────────────────────────────────────
  if (needsConfirm) {
    return (
      <main id="main-content" className="auth-container">
        <FocusGuard landmarkId="confirm-form" />
        <div className="auth-card">
          <h2>Verify your email</h2>
          <p className="auth-subtext">Enter the code we emailed to {email}.</p>
          <form onSubmit={handleConfirm} className="auth-form" id="confirm-form">
            <div className="form-group">
              <label htmlFor="confirm-code">Verification code</label>
              <input
                id="confirm-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={confirmCode}
                onChange={e => setConfirmCode(e.target.value)}
                required
                disabled={isLoading}
              />
            </div>
            {info && <div className="info-message" role="status">{info}</div>}
            {error && <div id="confirm-error" className="error-message" role="alert" aria-live="assertive">{error}</div>}
            <button type="submit" className="auth-button" disabled={isLoading}>
              {isLoading ? 'Verifying…' : 'Verify & sign in'}
            </button>
          </form>
          <div className="auth-links">
            <button type="button" className="link-button" onClick={() => resendConfirmationCode(email)}>
              Resend code
            </button>
          </div>
        </div>
      </main>
    );
  }

  // ── Render: main login ──────────────────────────────────────────────────────
  return (
    <main id="main-content" className="auth-container">
      <FocusGuard landmarkId="login-form" />
      <div className="auth-card">
        <h2>Login to AskYourCash</h2>

        {socialConfigured() && (
          <>
            <div className="oauth-buttons">
              <button
                type="button"
                className="oauth-btn oauth-btn--google"
                onClick={() => handleSocial('Google')}
                disabled={isLoading}
              >
                <svg className="oauth-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Continue with Google
              </button>
              <button
                type="button"
                className="oauth-btn oauth-btn--apple"
                onClick={() => handleSocial('SignInWithApple')}
                disabled={isLoading}
              >
                <svg className="oauth-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                Continue with Apple
              </button>
            </div>
            <div className="oauth-divider">
              <span>or sign in with email</span>
            </div>
          </>
        )}

        {/* Email / password */}
        <form onSubmit={handleSubmit} className="auth-form" id="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={isLoading || isLockedOut}
              aria-invalid={!!error}
              aria-describedby={error ? "login-error" : undefined}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={isLoading || isLockedOut}
                aria-invalid={!!error}
                aria-describedby={error ? "login-error" : undefined}
              />
              <PasswordToggle shown={showPassword} onToggle={() => setShowPassword(p => !p)} />
            </div>
          </div>

          {isLockedOut && (
            <div className="lockout-warning">
              <span role="alert">Too many failed attempts. Please wait before trying again.</span>
              <span aria-hidden="true"> Try again in {lockoutSecondsLeft}s.</span>
            </div>
          )}
          {!isLockedOut && error && (
            <div id="login-error" className="error-message" role="alert" aria-live="assertive">{error}</div>
          )}

          <button
            type="submit"
            className="auth-button"
            disabled={isLoading || isLockedOut}
          >
            {isLoading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="auth-links">
          <p><Link to="/forgot-password">Forgot your password?</Link></p>
          <p>Don't have an account? <Link to="/register">Register here</Link></p>
        </div>
      </div>
    </main>
  );
};

export default Login;
